import * as d3 from 'd3';
import type { SizedNode } from '../types';

export const ROOT_NODE_ID = 'root';

/** The unlabelled node d3 needs a single one of, holding whatever the traversal produced. */
const toRootNode = (nodes: SizedNode[]): SizedNode => ({
  id: ROOT_NODE_ID,
  modelId: null,
  label: null,
  subLabel: null,
  nodes,
  color: null,
  isDark: false,
  representationData: null,
  value: 0,
});

const valueOf = (node: d3.HierarchyNode<SizedNode>): number => node.value ?? 0;

const byDescendingValue = (
  a: d3.HierarchyNode<SizedNode>,
  b: d3.HierarchyNode<SizedNode>
): number => valueOf(b) - valueOf(a);

/**
 * Three library calls over data that already knows what it weighs.
 *
 * Built inside each visualization's layout memo rather than in the view model, because
 * `d3.treemap`, `d3.pack` and `d3.partition` write their geometry onto these nodes: a hierarchy
 * the stream owned would be state the render pass mutates, and a second reader - an export, a
 * tooltip, a split pane - would see whatever the last render left on it. Built here, each memo
 * mutates only what it made.
 *
 * Shared rather than inlined three times so the rollup and the sort order cannot come to differ
 * between the views: all three read `value` and nothing else about how it was arrived at.
 */
export const buildHierarchy = (
  nodes: SizedNode[]
): d3.HierarchyNode<SizedNode> =>
  d3
    .hierarchy(toRootNode(nodes), ({ nodes: children }) => children)
    .sum(({ value }) => value)
    .sort(byDescendingValue);
